import * as Phaser from "phaser-ce"
import { ClassStoreSA } from "../../../../react/classes/ClassStoreSA"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { TransitionState } from "../../../GameState"
import { BackgroundSprite } from "../../../generic-sprites/BackgroundSprite"
import { ExpandoSprite } from "../../../generic-sprites/ExpandoSprite"
import { GenericAssets, loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { Sound } from "../../../generic-sprites/lip-sync/Sound"
import { Word } from "../../../generic-sprites/lip-sync/Word"
import { ScientistSprite } from "../../../generic-sprites/ScientistSprite"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Transitions } from "../../transitions/Transitions"
import { AddictionRepo } from "../AddictionRepo"
import { AddictionMainAssets, loadMainAssets } from "./AddictionMainAssets"
import { AddPos } from "./AddPos"

export class AddictionMainState extends Phaser.State {

    private static readonly WAG = "wag"

    mental: ExpandoSprite
    physical: ExpandoSprite
    cravingsAndWithdrawal: ExpandoSprite
    nicotineAndAddiction: ExpandoSprite

    scientist: ScientistSprite

    private readonly introWords: Array<Word> = [
        new Word(190, 910, [Sound.E, Sound.O], "Hello"),
        new Word(910, 1390, [Sound.A, Sound.E, Sound.MBP], "and welcome"),
        new Word(1391, 1850, [Sound.CON, Sound.MBP, Sound.A, Sound.MBP], "to my lab."),
        new Word(2721, 3071, [Sound.O, Sound.A], "Today"),
        new Word(3072, 3441, [Sound.E, Sound.O], "we're going"),
        new Word(3442, 3871, [Sound.O, Sound.L, Sound.MBP, Sound.O], "to learn about"),
        new Word(3971, 4471, [Sound.CON, Sound.O, Sound.CON], "nicotine"),
        new Word(4901, 5021, [Sound.A], "and"),
        new Word(5171, 5541, [Sound.MBP, Sound.O, Sound.E], "the process"),
        new Word(5601, 6081, [Sound.FV, Sound.CON, Sound.O], "of addiction."),

        new Word(7022, 7222, [Sound.CON, Sound.A], "There are"),
        new Word(7302, 7432, [Sound.O], "two"),
        new Word(7512, 7672, [Sound.CON, Sound.E], "types"),
        new Word(7673, 8292, [Sound.FV, Sound.CON, Sound.O], "of addiction:"),
        new Word(8712, 9152, [Sound.FV, Sound.L], "Physical"),
        new Word(9282, 9802, [Sound.A, Sound.MBP, Sound.E, Sound.L], "and Mental."),

        new Word(10813, 10953, [Sound.CON], "Click"),
        new Word(11003, 11503, [Sound.O, Sound.A], "on any"),
        new Word(11504, 11813, [Sound.FV, Sound.E], "of the"),
        new Word(11893, 12273, [Sound.E, Sound.MBP, Sound.E, Sound.CON], "experiments"),
        new Word(12323, 13133, [Sound.O, Sound.FV, Sound.O, Sound.CON], "to find out about these"),
        new Word(13183, 13343, [Sound.O], "two"),
        new Word(13423, 13573, [Sound.MBP], "type"),
        new Word(13574, 14273, [Sound.FV, Sound.CON, Sound.O], "s of addiction"),
        new Word(14634, 14954, [Sound.A, Sound.CON], "and why"),
        new Word(15014, 15534, [Sound.MBP, Sound.L, Sound.E, Sound.O, Sound.CON], "people get hooked"),
        new Word(15654, 16304, [Sound.O, Sound.CON, Sound.O, Sound.CON], "on nicotine"),
        new Word(16454, 16664, [Sound.O], "so"),
        new Word(16714, 17154, [Sound.E, Sound.L], "easily."),
    ]

    private readonly postExperimentWords: Array<Word> = [
        new Word(461, 778, [Sound.O, Sound.CON], "Now that"),
        new Word(844, 939, [Sound.U, Sound.FV], "you've"),
        new Word(1057, 1290, [Sound.FV, Sound.CON], "finished"),
        new Word(1361, 2077, [Sound.CON, Sound.E, Sound.MBP, Sound.E, Sound.CON], "this experiment"),
        new Word(2557, 2662, [Sound.CON], "check"),
        new Word(2739, 2861, [Sound.O], "out"),
        new Word(2962, 3354, [Sound.CON, Sound.E, Sound.CON], "the rest."),
    ]

    private readonly doneWords: Array<Word> = [
        new Word(386, 717, [Sound.A, Sound.CON, Sound.O], "Thanks for"),
        new Word(718, 1132, [Sound.FV, Sound.CON], "visiting"),
        new Word(1133, 1704, [Sound.MBP, Sound.A, Sound.MBP], "my lab"),
        new Word(1817, 2166, [Sound.O, Sound.L, Sound.CON], "to learn"),
        new Word(2220, 2318, [Sound.MBP, Sound.O], "about"),
        new Word(2412, 2923, [Sound.CON, Sound.O, Sound.CON], "nicotine"),
        new Word(2924, 3433, [Sound.A, Sound.CON], "addiction."),
        new Word(4337, 4431, [Sound.U], "You"),
        new Word(4475, 4800, [Sound.A, Sound.E], "can see"),
        new Word(4892, 5143, [Sound.A, Sound.FV], "any of"),
        new Word(5246, 5310, [Sound.CON], "the"),
        new Word(5359, 6376, [Sound.E, Sound.MBP, Sound.E, Sound.CON, Sound.A, Sound.CON], "experiments again"),
        new Word(6813, 6896, [Sound.O], "or"),
        new Word(6990, 7117, [Sound.CON], "click"),
        new Word(7221, 7555, [Sound.E, Sound.CON], "Next"),
        new Word(7815, 8326, [Sound.O, Sound.CON, Sound.U], "to continue."),
    ]

    preload() {
        loadGenericAssets(this.game)
        loadMainAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        this.game.physics.startSystem(Phaser.Physics.ARCADE)

        this.game.add.existing(new BackgroundSprite(this.game, AddictionMainAssets.BACKGROUND))

        this.cravingsAndWithdrawal = new ExpandoSprite(this.game, AddPos.cravingsAndWithdrawalX(),
            AddPos.cravingsAndWithdrawalY(), AddictionMainAssets.CRAVINGS_AND_WITHDRAWAL, {scale: 0.8, on: false})
        this.cravingsAndWithdrawal.addClickHereText(AddPos.cravingsAndWithdrawalClickHereX(),
            AddPos.cravingsAndWithdrawalClickHereY())
        this.cravingsAndWithdrawal.addTitle("Cravings and\nWithdrawal", {
            x: AddPos.cravingsAndWithdrawalTitleX(),
            y: AddPos.cravingsAndWithdrawalTitleY(), linesHigh: 2
        })
        this.cravingsAndWithdrawal.addOnClick(() => {
            this.game.state.start(TransitionState.ADDICTION_CRAVINGS_AND_WITHDRAWAL)
        })

        this.nicotineAndAddiction = new ExpandoSprite(this.game, AddPos.nicotineAndAddictionX(),
            AddPos.nicotineAndAddictionY(), AddictionMainAssets.NICOTINE_AND_ADDICTION, {scale: 0.85, on: false})
        this.nicotineAndAddiction.addClickHereText(AddPos.nicotineAndAddictionClickHereX(),
            AddPos.nicotineAndAddictionClickHereY(), false)
        this.nicotineAndAddiction.addTitle("Nicotine and\nAddiction", {
            x: AddPos.nicotineAndAddictionTitleX(),
            y: AddPos.nicotineAndAddictionTitleY(), linesHigh: 2
        })
        this.nicotineAndAddiction.addOnClick(() => {
            this.game.state.start(TransitionState.ADDICTION_NICOTINE)
        })

        let audio: string
        let words: Array<Word>
        let nextButton: Phaser.Button
        if (this.allExperimentsComplete()) {
            audio = AddictionMainAssets.DONE

            nextButton = this.game.add.button(AddPos.backToMuseumX(), AddPos.backToMuseumY(),
                GenericAssets.NEXT_BUTTON, () => {
                    AddictionRepo.destroy()
                    Transitions.startOutro(this.game, ClassStoreSA.instance.is5thGrade() ? ActivityKey.ADDICTION_MAIN : ActivityKey.ADDICTION_201)
                    this.scientist.audio.stop()
                }, this, 1, 0)
            Scale.scaleSprite(nextButton, 0.9)
            words = this.doneWords
        } else if (this.anyExperimentComplete()) {
            audio = AddictionMainAssets.POST_EXPERIMENT
            words = this.postExperimentWords
        } else {
            audio = AddictionMainAssets.INTRO
            words = this.introWords
        }

        this.scientist = new ScientistSprite(this.game, AddPos.scientistX(), AddPos.scientistY(), audio, 0.79, words,
            undefined, undefined, undefined, nextButton)

        this.mental =
            new ExpandoSprite(this.game, AddPos.mentalX(), AddPos.mentalY(), AddictionMainAssets.MENTAL, {
                scale: 0.85,
                on: false
            })
        this.mental.animations.add(AddictionMainState.WAG)
        this.mental.animations.play(AddictionMainState.WAG, 4, true)
        this.mental.addClickHereText(AddPos.mentalClickHereX(), AddPos.mentalClickHereY())
        this.mental.addTitle("Mental Addiction", {x: AddPos.mentalTitleX(), y: AddPos.mentalTitleY()})

        this.mental.addOnClick(() => {
            this.game.state.start(TransitionState.ADDICTION_MENTAL)
        })

        this.physical = new ExpandoSprite(this.game, AddPos.physicalX(), AddPos.physicalY(),
            AddictionMainAssets.PHYSICAL, {scale: 0.75, on: false})
        this.physical.addClickHereText(AddPos.physicalClickHereX(), AddPos.physicalClickHereY(), false)
        this.physical.addTitle("Physical Addiction", {x: AddPos.physicalTitleX(), y: AddPos.physicalTitleY()})

        this.physical.addOnClick(() => {
            this.game.state.start(TransitionState.ADDICTION_PHYSICAL)
        })

        // Start all the start-of-scene stuff.

        this.scientist.audio.onStop.addOnce(() => {
            this.turnExperimentsOn()
        }, this)

        this.grayOutCompleted()
    }

    private turnExperimentsOn = () => {
        this.mental.on = true
        this.physical.on = true
        this.cravingsAndWithdrawal.on = true
        this.nicotineAndAddiction.on = true
    }

    private grayOutCompleted = () => {
        if (AddictionRepo.instance.isMentalComplete()) {
            this.mental.grayOut()
        }
        if (AddictionRepo.instance.isPhysicalComplete()) {
            this.physical.grayOut()
        }
        if (AddictionRepo.instance.isCravingsAndWithdrawalComplete()) {
            this.cravingsAndWithdrawal.grayOut()
        }
        if (AddictionRepo.instance.isNicotineAndAddictionComplete()) {
            this.nicotineAndAddiction.grayOut()
        }
    }

    private anyExperimentComplete = (): boolean => {
        return AddictionRepo.instance.isMentalComplete() || AddictionRepo.instance.isPhysicalComplete() ||
            AddictionRepo.instance.isCravingsAndWithdrawalComplete() || AddictionRepo.instance.isNicotineAndAddictionComplete()
    }

    private allExperimentsComplete = (): boolean => {
        return AddictionRepo.instance.isMentalComplete() && AddictionRepo.instance.isPhysicalComplete() &&
            AddictionRepo.instance.isCravingsAndWithdrawalComplete() && AddictionRepo.instance.isNicotineAndAddictionComplete()
    }

}
